import type { LogisticsCalculationCurrency } from './content.js';

const GROUP_SUBTITLES = {
  USD: 'оплата в долларах',
  CNY: 'расчёт в юанях, пересчёт в доллары',
  RUB: 'оплата в рублях',
} as const satisfies Record<LogisticsCalculationCurrency, string>;

/** The three payment modes as the tag printed beside each payable amount. */
const PAYMENT_TAGS = {
  novat: 'без НДС',
  vat_included: 'с НДС',
  individual: 'физическое лицо',
} as const;

// WHY: customer document copy stays outside web i18n so the manager preview
// and public page cannot drift from one another.
export const LOGISTICS_CALCULATION_SHEET_CHROME = {
  title: 'Расчёт по логистике',
  executorLabel: 'Исполнитель',
  executorName: 'Магистраль Импорт',
  customerLabel: 'Заказчик',
  dueLabel: 'Оплатить до',
  grandCaption: 'Итого к оплате',
  allRubCaption: 'Справочно, всё в рублях',
  summaryHeaders: ['Груз', '$', '¥', '₽'] as const,
  cargoCaption: 'Груз',
  metadata: {
    seats: 'Места',
    weight: 'Вес',
    volume: 'Объём',
    transportType: 'Транспорт',
  },
  sections: {
    chinaLogistics: 'Логистика из Китая',
    chinaExpenses: 'Расходы по Китаю',
    rfExpenses: 'Расходы по РФ',
    rfLogistics: 'Логистика по РФ',
  },
  commonServices: 'Общие услуги',
  managerNote: 'Примечание',
  manager: 'Менеджер',
  footer: ['Не является счётом на оплату', 'Документ информационный'] as const,
  /**
   * Copy the approved ТТН layout adds, kept apart from the keys above.
   *
   * WHY a separate group: the keys above are read by the published sheet and by
   * both PDF renderers, so their wording is frozen for documents already sent to
   * customers. This group is the copy of `design-refs/ttn/raschet-forma.html`,
   * which reuses a key above wherever the sample says the same words
   * (`title`, `executorLabel`, `customerLabel`, `dueLabel`, `grandCaption`,
   * `allRubCaption`, `cargoCaption`, `sections`, `commonServices`, `manager`).
   */
  document: {
    // The masthead is `executorName`; only the line under it is new copy.
    tagline: 'логистика из Китая',
    informationalCaption: 'Документ информационный',
    datePrefix: 'от',
    cargoCountCaption: 'грузов в расчёте',
    // Russian count forms for the totals line: one, two-to-four, five-and-up.
    cargoCountForms: ['груз', 'груза', 'грузов'] as const,
    seatCountForms: ['место', 'места', 'мест'] as const,
    companyLabel: 'Компания',
    contactLabel: 'Контактное лицо',
    phoneLabel: 'Тел.',
    telegramLabel: 'Telegram',
    paymentSplitCaption: 'двумя платежами',
    dueCaption: 'оплатить до',
    rateCaption: 'Курс документа',
    rubPerUsdCaption: '₽ за $1',
    rubPerCnyCaption: '₽ за ¥1',
    allRubCaption: 'справочно всё в рублях',
    summaryTitle: 'Сводка по грузам',
    summaryHeaders: [
      'Артикул',
      'Наименование',
      'Мест',
      'Вес, кг',
      'Объём, м³',
      'В долларах',
      'В юанях',
      'В рублях',
    ] as const,
    grandRowCaption: 'Всего по расчёту',
    commonServicesRowCaption: 'Общие услуги по расчёту',
    detailsTitle: 'Детализация по грузам',
    detailHeaders: ['№', 'Услуга', 'Кол-во', 'Ед.', 'Ставка', 'Сумма'] as const,
    photoPlaceholder: 'Фото',
    // A group row states how its lines are priced, so the wording follows the
    // currency of the group. `sectionSubtitles` is the sample's reading of the
    // four sections; a group priced in another currency takes `groupSubtitles`.
    groupSubtitles: GROUP_SUBTITLES,
    sectionSubtitles: {
      chinaLogistics: GROUP_SUBTITLES.USD,
      chinaExpenses: GROUP_SUBTITLES.CNY,
      rfExpenses: GROUP_SUBTITLES.RUB,
      rfLogistics: GROUP_SUBTITLES.RUB,
    },
    cargoTotalCaption: 'Итого по грузу',
    commonServicesBadge: 'Общие',
    commonServicesTitle: 'Услуги по всей партии',
    commonServicesHint: 'относятся ко всему расчёту, не к отдельному грузу',
    commonServicesTotalCaption: 'Итого по общим услугам',
    pricingBasis: { weight: 'расчёт по весу', volume: 'расчёт по объёму' },
    daysUnit: 'дн.',
    densityUnit: 'кг/м³',
    managerNoteCaption: 'Комментарий менеджера',
    signatureCaption: 'Расчёт составил',
    // The landscape table of the approved reference: its four spanning group
    // titles and the eleven column headers under them.
    tableGroupHeaders: [
      'Груз',
      'Логистика Китай → РФ',
      'Логистика по РФ',
      'Итого по грузу',
    ] as const,
    tableColumnHeaders: [
      'Артикул · наименование',
      'Дорога',
      'Мест',
      'Вес · объём · плотность',
      'Отправлен\nприбыл',
      'Перевозка',
      'Страхование',
      'Упаковка',
      'Доп. расходы',
      'ПРР',
      'Доставка до клиента',
    ] as const,
    payableCaption: 'К оплате',
    payableTags: PAYMENT_TAGS,
    disclaimer: [
      'Не является счётом на оплату',
      'Документ отражает стоимость логистических услуг',
    ] as const,
  },
} as const;
